import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { GripVertical, X } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageWithFallback from '../../../components/ImageWithFallback';
import ProductSearchInput from '../../../components/ProductSearchInput';
import { listKitItems, createKitItem, updateKitItem, removeKitItem, reorderKitItems } from '../../../utils/queries';
import { formatCurrency } from '../../../utils/format';

function KitItemRow({ item, onQuantityChange, onRemove }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.kitItemId });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};
	const price = item.componentPointCost != null ? `${item.componentPointCost} pts` : item.componentUnitPriceCents != null ? formatCurrency(item.componentUnitPriceCents) : '';

	return (
		<div ref={setNodeRef} style={style} className='flex items-center gap-3 border border-gray-200 rounded-md px-3 py-2 bg-white'>
			<button type='button' className='text-gray-400 hover:text-gray-600 cursor-grab' {...attributes} {...listeners}>
				<GripVertical className='size-4' />
			</button>
			<div className='size-10 shrink-0 rounded-md bg-gray-100 overflow-hidden'>
				<ImageWithFallback src={item.imageUrl} alt={item.componentName} className='size-full object-cover' fallbackClassName='size-full' />
			</div>
			<div className='min-w-0 flex-1'>
				<p className='text-sm text-black truncate'>{item.componentName}</p>
				<p className='text-xs text-gray-400 truncate'>{item.componentSku || price}</p>
			</div>
			<input
				type='number'
				min='1'
				className='form-input w-16'
				value={item.quantity}
				onChange={(e) => onQuantityChange(item.kitItemId, e.target.value)}
			/>
			<button type='button' className='text-gray-400 hover:text-gray-600' onClick={() => onRemove(item.kitItemId)}>
				<X className='size-4' />
			</button>
		</div>
	);
}

export default function KitItemsTab({ product }) {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

	const refresh = () => {
		setLoading(true);
		return listKitItems(product.productId).then((result) => {
			if (result?.success) setItems(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product.productId]);

	const handleAdd = async (componentProduct) => {
		const result = await createKitItem(product.productId, { componentProductId: componentProduct.productId, quantity: 1 });
		if (!result?.success) {
			toast.error(result?.error?.message || 'Failed to add product to kit.');
			return;
		}
		refresh();
	};

	const handleQuantityChange = async (kitItemId, quantity) => {
		const parsed = Math.max(1, Number(quantity) || 1);
		setItems((prev) => prev.map((i) => (i.kitItemId === kitItemId ? { ...i, quantity: parsed } : i)));
		await updateKitItem(kitItemId, { quantity: parsed });
	};

	const handleRemove = async (kitItemId) => {
		setItems((prev) => prev.filter((i) => i.kitItemId !== kitItemId));
		await removeKitItem(kitItemId);
	};

	const handleDragEnd = async (event) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = items.findIndex((i) => i.kitItemId === active.id);
		const newIndex = items.findIndex((i) => i.kitItemId === over.id);
		const reordered = arrayMove(items, oldIndex, newIndex);
		setItems(reordered);
		await reorderKitItems(
			product.productId,
			reordered.map((i) => i.kitItemId),
		);
	};

	return (
		<div className='max-w-2xl space-y-4'>
			<ProductSearchInput
				storeId={product.storeId}
				excludeProductIds={items.map((i) => i.componentProductId)}
				onSelect={handleAdd}
			/>

			{loading && <p className='text-sm text-gray-400'>Loading...</p>}

			{!loading && items.length === 0 && <p className='text-sm text-gray-400'>No products in this kit yet.</p>}

			{!loading && items.length > 0 && (
				<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext items={items.map((i) => i.kitItemId)} strategy={verticalListSortingStrategy}>
						<div className='space-y-2'>
							{items.map((item) => (
								<KitItemRow
									key={item.kitItemId}
									item={item}
									onQuantityChange={handleQuantityChange}
									onRemove={handleRemove}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
			)}

			{!loading && items.length > 0 && (
				<p className='text-xs text-gray-400 text-right'>
					{items.length} product{items.length === 1 ? '' : 's'}
				</p>
			)}
		</div>
	);
}
